import { Action, createReducer, on } from '@ngrx/store';
import * as fromActions from './image-browser.actions';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { fullImagePath } from 'src/app/modules/shared/utils/fullImagePath';
import { getTimelineIndexByDate } from './image-browser.utils';

export class ExtraCameraImage {
  id: number;
  date: dayjs.Dayjs;
  url: string;
  index: number;
  loading: boolean;
}

export interface ImageBrowserState {
  cameraId: number;
  periodId: string;
  cameraFilterRegion?: string;
  loopEnd: dayjs.Dayjs;
  loopStart: dayjs.Dayjs;
  start: dayjs.Dayjs;
  timeline: {
    date: dayjs.Dayjs;
    url: string;
    index: number;
  }[];
  extraCamerasIds: number[];
  extraCamerasTimeline: {
    [cameraId: number]: ExtraCameraImage[];
  };
  extraCamerasLoading: {
    [cameraId: number]: boolean;
  };
  currentFrame: number;
  playing: boolean;
  currentFrameLoaded: boolean;
  loadingInitial: boolean;
  loadingAfter: boolean;
  loadingBefore: boolean;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  looping: boolean;

  loopStartIndex: number;
  loopEndIndex: number;
  //a jump waiting on frames that have not paged in yet, and whether we gave up reaching it
  pendingJump: number | null;
  jumpUnreachable: boolean;
  //valet reads two stills per event rather than a clip, so it opens paused and offers no play
  autoplay: boolean;
}

export const initialImageBrowserState: ImageBrowserState = {
  cameraId: null,
  periodId: null,
  loopEnd: null,
  loopStart: null,
  start: null,
  timeline: [],
  extraCamerasIds: [],
  extraCamerasTimeline: {},
  extraCamerasLoading: {},
  currentFrame: 0,
  playing: false,
  currentFrameLoaded: false,
  loadingAfter: false,
  loadingBefore: false,
  loadingInitial: false,
  hasMoreAfter: true,
  hasMoreBefore: true,
  //Loop serving replays a window that keeps growing as more frames page in, so it wanders onto
  //other vehicles. Off unless the viewer asks for it.
  looping: false,
  loopStartIndex: 0,
  loopEndIndex: 0,
  pendingJump: null,
  jumpUnreachable: false,
  autoplay: true,
};

/**
 * The frame closest to the given moment, or null when the frames we hold do not span it. Events are
 * timed to the second and frames are not, so an exact match finds nothing far more often than not.
 */
function nearestFrame(timeline: ImageBrowserState['timeline'], timestamp: number) {
  if (timeline.length === 0) return null;
  if (timestamp < timeline[0].date.valueOf() || timestamp > timeline[timeline.length - 1].date.valueOf()) return null;

  return timeline.reduce((closest, item) =>
    Math.abs(item.date.valueOf() - timestamp) < Math.abs(closest.date.valueOf() - timestamp) ? item : closest,
  );
}

const reducer = createReducer(
  initialImageBrowserState,
  on(fromActions.initFromTransaction, (state, { transaction, periodId, filterRegion, autoplay }) =>
    produce(state, state => {
      state.autoplay = autoplay ?? true;
      state.periodId = periodId;
      state.cameraId = transaction.cameraId;
      state.cameraFilterRegion = filterRegion;

      const middleIndex = Math.floor(transaction.images.length / 2);

      state.loopStart = dayjs(transaction.date).add(-middleIndex, 'second');
      state.loopEnd = dayjs(transaction.date).add(transaction.images.length - middleIndex, 'second');
      state.start = dayjs(transaction.date);

      state.timeline = transaction.images.map((img, index) => ({
        date: dayjs(transaction.date)
          .add(index - middleIndex, 'second')
          .startOf('second'),
        url: fullImagePath(img),
        index,
      }));

      state.currentFrame = 0;
      state.playing = state.autoplay;
      //jumping to the paired event comes through here, and the loop it would inherit belongs to
      //the event we just left, so it replays a window that is no longer this car's
      state.looping = false;

      state.loopStartIndex = 0;
      state.loopEndIndex = state.timeline.length - 1;
      state.extraCamerasIds = [];
      state.extraCamerasTimeline = {};
      //paging belongs to the camera and timeline being opened, carrying it over from the last one
      //decides what a fresh timeline is allowed to load
      state.loadingBefore = false;
      state.loadingAfter = false;
      state.hasMoreBefore = true;
      state.hasMoreAfter = true;
      state.pendingJump = null;
      state.jumpUnreachable = false;
    }),
  ),
  on(fromActions.init, (state, { cameraId, loopEnd, loopStart, start, periodId, filterRegion, autoplay }) =>
    produce(state, state => {
      state.autoplay = autoplay ?? true;
      state.periodId = periodId;
      state.cameraId = cameraId;
      state.cameraFilterRegion = filterRegion;
      state.loopEnd = loopEnd;
      state.loopStart = loopStart;
      state.start = start;

      state.loadingInitial = true;

      state.timeline = [];

      state.currentFrame = 0;
      state.playing = state.autoplay;

      state.loopStartIndex = 0;
      state.loopEndIndex = state.timeline.length - 1;
      state.extraCamerasIds = [];
      state.extraCamerasTimeline = {};
      //paging belongs to the camera and timeline being opened, carrying it over from the last one
      //decides what a fresh timeline is allowed to load
      state.loadingBefore = false;
      state.loadingAfter = false;
      state.hasMoreBefore = true;
      state.hasMoreAfter = true;
      state.pendingJump = null;
      state.jumpUnreachable = false;
    }),
  ),
  on(fromActions.goNext, (state, { pause }) =>
    produce(state, state => {
      if (state.loopEndIndex === state.loopStartIndex) return;
      if (state.looping && state.loopEndIndex === state.currentFrame) {
        state.currentFrame = state.loopStartIndex;
      } else {
        state.currentFrame = Math.min(state.currentFrame + 1, state.timeline.length - 1);
        if (state.currentFrame === state.timeline.length - 1) {
          state.playing = false;
        }
      }
      if (pause) {
        state.playing = false;
        state.looping = false;
      }
      state.currentFrameLoaded = false;
    }),
  ),
  on(fromActions.goPrev, state =>
    produce(state, state => {
      state.playing = false;
      state.looping = false;
      if (state.looping && state.loopStartIndex === state.currentFrame) {
        state.currentFrame = state.loopEndIndex;
      } else {
        state.currentFrame = Math.max(state.currentFrame - 1, 0);
      }
      state.currentFrameLoaded = false;
    }),
  ),

  on(fromActions.goNextN, (state, { count }) =>
    produce(state, state => {
      state.currentFrame = Math.min(state.currentFrame + count, state.timeline.length - 1);
      state.currentFrameLoaded = false;
      state.looping = false;

      if (state.currentFrame === state.timeline.length - 1) {
        state.playing = false;
      }
    }),
  ),
  on(fromActions.goPrevN, (state, { count }) =>
    produce(state, state => {
      state.currentFrame = Math.max(state.currentFrame - count, 0);
      state.currentFrameLoaded = false;
      state.looping = false;

      if (state.currentFrame === 0) {
        state.playing = false;
      }
    }),
  ),
  on(fromActions.play, state =>
    produce(state, state => {
      state.playing = true;
    }),
  ),
  on(fromActions.pause, state =>
    produce(state, state => {
      state.playing = false;
    }),
  ),
  on(fromActions.togglePlay, state =>
    produce(state, state => {
      state.playing = !state.playing;
    }),
  ),
  on(fromActions.goTo, (state, { index }) =>
    produce(state, state => {
      state.playing = false;
      state.currentFrame = index;
      state.currentFrameLoaded = false;
    }),
  ),
  on(fromActions.goToTimestamp, (state, { timestamp }) =>
    produce(state, state => {
      const targetFrame = nearestFrame(state.timeline, timestamp);
      //the frames we hold do not span that moment. Remember it so the jump can be tried again once
      //more have paged in, rather than parking on a frame that never loads
      if (!targetFrame) {
        state.pendingJump = timestamp;
        return;
      }

      state.pendingJump = null;
      state.jumpUnreachable = false;
      state.currentFrame = targetFrame.index;
      state.playing = state.autoplay;
      state.currentFrameLoaded = true;
      state.loopStartIndex = targetFrame.index - 2;
      state.loopEndIndex = targetFrame.index + 2;
    }),
  ),
  on(fromActions.jumpUnreachable, state =>
    produce(state, state => {
      state.pendingJump = null;
      state.jumpUnreachable = true;
    }),
  ),
  on(fromActions.loadImagesBefore, state =>
    produce(state, state => {
      state.loadingBefore = true;
    }),
  ),
  on(fromActions.loadImagesAfter, state =>
    produce(state, state => {
      state.loadingAfter = true;
    }),
  ),
  on(fromActions.loadImagesBeforeSuccess, (state, { images, before, fromIndex }) =>
    produce(state, state => {
      state.loadingBefore = false;

      //the last image is the frame we already hold, so a response of one (or none) adds nothing.
      //Reporting more to come off a response we dropped entirely re-arms the loader against an
      //unchanged timeline, and it asks for the same window forever.
      const added = images.slice(0, -1);
      state.hasMoreBefore = added.length > 0;
      if (added.length === 0) return;

      state.timeline = [
        ...added.map(item => ({
          date: dayjs(item.parsedDate),
          url: fullImagePath(item.fileUrl),
          index: 0,
        })),
        ...state.timeline,
      ];
      state.currentFrame += added.length;
      state.timeline.forEach((item, index) => (item.index = index));
      state.loopStartIndex += added.length;
      state.loopEndIndex += added.length;
    }),
  ),
  on(fromActions.loadImagesAfterSuccess, (state, { images, after, fromIndex }) =>
    produce(state, state => {
      state.loadingAfter = false;

      //same here, the first image is the frame we already hold
      const added = images.slice(1);
      state.hasMoreAfter = added.length > 0;
      if (added.length === 0) return;

      state.timeline = [
        ...state.timeline,
        ...added.map(item => ({
          date: dayjs(item.parsedDate),
          url: fullImagePath(item.fileUrl),
          index: 0,
        })),
      ];
      state.timeline.forEach((item, index) => (item.index = index));
    }),
  ),
  on(fromActions.imageLoaded, state =>
    produce(state, state => {
      state.currentFrameLoaded = true;
    }),
  ),

  on(fromActions.toggleLoop, state =>
    produce(state, state => {
      state.looping = !state.looping;
      if (state.looping && (state.currentFrame < state.loopStartIndex || state.currentFrame > state.loopEndIndex)) {
        state.currentFrame = state.loopStartIndex;
      }
    }),
  ),
  on(fromActions.loadInitialImagesSuccess, (state, { images }) =>
    produce(state, state => {
      state.loadingInitial = false;
      state.timeline = images.map((img, index) => ({
        date: dayjs(img.parsedDate).startOf('second'),
        url: fullImagePath(img.fileUrl),
        index,
      }));
      state.currentFrame = getTimelineIndexByDate(state.timeline, state.start);
      state.playing = state.autoplay;

      state.loopStartIndex = getTimelineIndexByDate(state.timeline, state.loopStart);
      state.loopEndIndex = getTimelineIndexByDate(state.timeline, state.loopEnd);
    }),
  ),
  on(fromActions.selectedCameraChanged, (state, { cameraIds }) =>
    produce(state, state => {
      state.playing = false;
      cameraIds.forEach(cameraId => {
        let cameraTimeline = state.extraCamerasTimeline[cameraId];
        if (!cameraTimeline) {
          state.extraCamerasTimeline[cameraId] = [];
        }
      });
      state.extraCamerasIds = cameraIds;
    }),
  ),
  on(fromActions.loadExtraCamera, (state, action) =>
    produce(state, state => {
      state.extraCamerasLoading[action.cameraId] = true;
      let cameraTimeline = state.extraCamerasTimeline[action.cameraId];
      if (!cameraTimeline) {
        state.extraCamerasTimeline[action.cameraId] = [];
      }
    }),
  ),
  on(fromActions.loadExtraCameraSuccess, (state, action) =>
    produce(state, state => {
      let imgs = action.images.map(
        item =>
          ({
            date: dayjs(item.parsedDate),
            url: fullImagePath(item.fileUrl),
            index: 0,
            id: action.cameraId,
          }) as ExtraCameraImage,
      );
      state.extraCamerasTimeline[action.cameraId].push(...imgs);
      state.extraCamerasTimeline[action.cameraId] = state.extraCamerasTimeline[action.cameraId].map(item => ({
        ...item,
        index: getTimelineIndexByDate(state.timeline, item.date),
      }));
      state.extraCamerasLoading[action.cameraId] = false;
    }),
  ),
);

export function imageBrowserReducer(state: ImageBrowserState, action: Action) {
  return reducer(state, action);
}
